import { useMutation, useQueryClient } from 'react-query';
import moment from 'moment';
import * as api from '../../services/api';
import { HELP_DESK_MESSAGE_TYPES, HELP_DESK_SENDER_TYPES } from '../../constants/helpDesk';
import useUser from '../auth/useUser';
import { getHelpDeskMessagesQueryKey } from './useHelpDeskMessages';
import { HELP_DESK_TICKETS_QUERY_KEY } from './useHelpDeskTickets';

/**
 * Mutation hook for sending a help desk message with optimistic update.
 * Prepends an optimistic message, rolls back on error, invalidates on success.
 * @param {{ accountId: string|number, ticketId: string|number }} params
 * @param {{ onError?: Function }} [options]
 */
const useHelpDeskSendMessage = ({ accountId, ticketId }, { onError } = {}) => {
  const queryClient = useQueryClient();
  const { data: { id: currentAdminId, firstName, lastName } = {} } = useUser();
  const messagesQueryKey = getHelpDeskMessagesQueryKey(accountId, ticketId);

  const { mutate: sendMessage, mutateAsync: sendMessageAsync, isLoading } = useMutation(
    (body) => api.sendHelpDeskMessage({ accountId, ticketId, ...body }),
    {
      onMutate: async (body) => {
        await queryClient.cancelQueries(messagesQueryKey);
        const previousMessages = queryClient.getQueryData(messagesQueryKey);
        const optimisticMsg = {
          id: `optimistic-${Date.now()}`,
          ticketId,
          senderType: HELP_DESK_SENDER_TYPES.ADMIN,
          senderAdminId: currentAdminId,
          senderName: [firstName, lastName].filter(Boolean).join(' '),
          messageType: body.isInternalNote ? HELP_DESK_MESSAGE_TYPES.INTERNAL_NOTE : HELP_DESK_MESSAGE_TYPES.MESSAGE,
          content: body.content,
          createdAt: moment.utc().format('YYYY-MM-DD HH:mm:ss'),
          _optimistic: true
        };
        queryClient.setQueryData(messagesQueryKey, (old) => {
          if (!old?.pages?.length) {
            return { pages: [{ data: [optimisticMsg], total: 1 }], pageParams: [0] };
          }
          const newTotal = (old.pages[0]?.total ?? 0) + 1;
          return {
            ...old,
            pages: old.pages.map((page, i) =>
              i === 0 ? { ...page, data: [optimisticMsg, ...page.data], total: newTotal } : { ...page, total: newTotal }
            )
          };
        });
        return { previousMessages };
      },
      onSuccess: () => {
        queryClient.invalidateQueries(messagesQueryKey);
        queryClient.invalidateQueries({
          predicate: (query) => Array.isArray(query.queryKey) && query.queryKey[0] === HELP_DESK_TICKETS_QUERY_KEY
        });
      },
      onError: (err, _body, context) => {
        if (context?.previousMessages) {
          queryClient.setQueryData(messagesQueryKey, context.previousMessages);
        }
        if (onError) onError(err);
      }
    }
  );

  return { sendMessage, sendMessageAsync, isLoading };
};

export default useHelpDeskSendMessage;
